<script setup lang="ts">
import { watch, onMounted, onUnmounted } from 'vue'
import { motion, AnimatePresence } from 'motion-v'
import { useChatbotStore } from '../stores/chatbotStore'
import { usePageContext } from '../composables/usePageContext'
import AiChatbotWindow from './AiChatbotWindow.vue'
import BackToTop from '@/components/ui/BackToTop.vue'

const store = useChatbotStore()
const { pageContext } = usePageContext()

watch(
  pageContext,
  (ctx) => {
    store.updatePageContext(ctx)
  },
  { immediate: true },
)

function toggle() {
  if (store.isOpen) {
    store.closeChat()
  } else {
    store.openChat()
  }
}

// Если чат был открыт до перехода/перезагрузки — переоткрываем и восстанавливаем
// переписку из сохранённой сессии (DEV-213).
onMounted(() => {
  store.restoreIfOpen()
})

// На размонтировании (переход по ссылке / перезагрузка) только закрываем сокет,
// НЕ завершая сессию: переписка должна пережить навигацию (DEV-213).
onUnmounted(() => {
  store.disconnect()
})
</script>

<template>
  <div class="chatbot-widget">
    <AnimatePresence>
      <motion.div
        v-if="store.isOpen"
        key="chat-window"
        class="chatbot-widget__window"
        :initial="{ opacity: 0, scale: 0.9, y: 20 }"
        :animate="{ opacity: 1, scale: 1, y: 0 }"
        :exit="{ opacity: 0, scale: 0.92, y: 14 }"
        :transition="{ type: 'spring', stiffness: 400, damping: 32 }"
      >
        <AiChatbotWindow @close="store.closeChat()" />
      </motion.div>
    </AnimatePresence>

    <motion.button
      class="chatbot-fab"
      :class="{ 'chatbot-fab--open': store.isOpen }"
      :hover="{ scale: 1.07 }"
      :press="{ scale: 0.94 }"
      :transition="{ type: 'spring', stiffness: 400, damping: 18 }"
      aria-label="Открыть чат с AI-ассистентом"
      @click="toggle"
    >
      <Transition name="fab-icon" mode="out-in">
        <svg v-if="!store.isOpen" key="chat" class="fab-icon" width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else key="close" class="fab-icon" width="22" height="22" viewBox="0 0 20 20" fill="none">
          <path d="M5 5L15 15M15 5L5 15" stroke="white" stroke-width="2.5" stroke-linecap="round" />
        </svg>
      </Transition>

      <span v-if="store.unreadCount > 0 && !store.isOpen" class="chatbot-fab__badge">
        {{ store.unreadCount > 9 ? '9+' : store.unreadCount }}
      </span>
    </motion.button>

    <BackToTop />
  </div>
</template>

<style scoped>
.chatbot-widget {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

.chatbot-widget__window {
  transform-origin: bottom right;
}

.chatbot-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: #2563eb;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.35);
}

.chatbot-fab--open {
  background: #1d4ed8;
}

.fab-icon {
  display: block;
}

.chatbot-fab__badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border: 2px solid white;
  animation: badge-pop 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* FAB icon swap */
.fab-icon-enter-active,
.fab-icon-leave-active {
  transition: transform 0.2s ease, opacity 0.15s ease;
}

.fab-icon-enter-from {
  opacity: 0;
  transform: rotate(-90deg) scale(0.5);
}

.fab-icon-leave-to {
  opacity: 0;
  transform: rotate(90deg) scale(0.5);
}

@keyframes badge-pop {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

@media (max-width: 480px) {
  .chatbot-widget {
    bottom: 16px;
    right: 16px;
  }

  .chatbot-fab {
    width: 52px;
    height: 52px;
  }
}
</style>
